import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Expenses() {
  // 🔥 Context Data
  const { expenses, loading, isAdmin, isAccount, isHR, currentUser } = useWebData();

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); // 'Month', 'Year', 'All'
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Dynamic Year
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // 🔥 Modal States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // Power User Check (For Approval Buttons)
  const canApprove = isAdmin || isAccount || isHR; 

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC (Fixed for Date Issues)
  // ==========================================
  useEffect(() => {
    if (!expenses) return;

    let result = [...expenses];

    // A. Search Filter (Date included)
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(item => {
            // Convert Date to Readable String (e.g., "25 Jan 2025")
            const dateStr = item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";

            return (
                (item.userName || "").toLowerCase().includes(lower) ||
                (item.type || "").toLowerCase().includes(lower) ||
                (item.status || "").toLowerCase().includes(lower) ||
                (item.amount || "").toString().includes(lower) ||
                dateStr.includes(lower) // 👈 Date Search
            );
        });
    }

    // 🔥 B. Date Filter (Universal String Match)
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); // 0-11

        result = result.filter(item => {
            const rawDate = item.createdAt || ""; 
            if (!rawDate) return false;

            let y = 0;
            let m = -1;

            // Logic 1: Try Standard Date Object
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
                y = d.getFullYear();
                m = d.getMonth();
            } 
            // Logic 2: Manual Parse (Fallback for DD-MM-YYYY)
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { // DD-MM-YYYY
                        y = parseInt(parts[2]);
                        m = parseInt(parts[1]) - 1;
                    } else if (parts[0].length === 4) { // YYYY-MM-DD
                        y = parseInt(parts[0]);
                        m = parseInt(parts[1]) - 1;
                    }
                }
            }

            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    // C. Sort (Newest First)
    result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    setFiltered(result);
  }, [expenses, searchTerm, viewMode, selectedDate, selectedYear]);

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Date: formatDate(i.createdAt),
        Employee_Name: i.userName || "Unknown",
        Expense_Type: i.type,
        Amount: i.amount,
        Status: i.status,
        From_Loc: i.from || "-",
        To_Loc: i.to || "-",
        Distance_KM: i.distance || "-",
        Remark: i.remark || "-",
        Location_Address: i.location?.address || (typeof i.location === 'string' ? i.location : "-"),
        Approved_By: i.approvedBy || "-",
        Image_Link: i.imageUrl || "No Image"
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses_Report");
    XLSX.writeFile(wb, `Expenses_Report_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  const updateStatus = async (id, status) => {
      if(window.confirm(`Mark expense as ${status}?`)) {
          try {
              await updateDoc(doc(db, "expenses", id), { 
                  status,
                  approvedBy: currentUser?.name || "Admin",
                  approvedAt: new Date().toISOString()
              });
              if(viewData) setViewData(null);
          } catch(e) { alert("Error updating status"); }
      }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "expenses", editData.id), {
              amount: editData.amount,
              type: editData.type,
              remark: editData.remark,
              status: editData.status
          });
          alert("Expense Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this expense?")) await deleteDoc(doc(db, "expenses", id));
  };

  // Helper: Month/Year Logic
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  // 🔥 Dynamic Years (2024 -> Future)
  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (iso) => {
    if(!iso) return "-";
    const date = new Date(iso);
    return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* --- HEADER & FILTERS --- */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#e74c3c", display:"flex", alignItems:"center"}}>
                💸 Expense Manager <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length} Records)</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Report</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            
            {/* VIEW MODE TOGGLES */}
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {/* DATE CHANGER (Month Mode) */}
            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {/* 🔥🔥 YEAR DROPDOWN (Auto Updates) 🔥🔥 */}
            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            {/* SEARCH */}
            <input type="text" placeholder="🔍 Search Date, Name, Type..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#e74c3c", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Details</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No records found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(row.createdAt)}</td>
                    <td style={{...tdStyle, fontWeight:"bold", color:"#2c3e50"}}>{row.userName}</td>
                    <td style={tdStyle}>
                        <span style={{padding:"2px 8px", borderRadius:"10px", backgroundColor:"#f0f0f0", fontSize:"11px"}}>{row.type}</span>
                    </td>
                    <td style={{...tdStyle, fontWeight:"bold", color:"#27ae60"}}>₹{row.amount}</td>
                    <td style={{...tdStyle, maxWidth:"200px", fontSize:"12px", color:"#555"}}>
                        {row.type === 'Travel' ? (
                            <span>🚗 {row.from} ➝ {row.to} ({row.distance}km)</span>
                        ) : (
                            <span>{row.remark ? row.remark.substring(0, 30) : '-'}</span>
                        )}
                    </td>
                    <td style={tdStyle}>
                        <span style={{
                            padding:"4px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:"bold",
                            backgroundColor: row.status==='Approved'?'#e8f5e9':(row.status==='Rejected'?'#ffebee':'#fff3e0'),
                            color: row.status==='Approved'?'green':(row.status==='Rejected'?'red':'orange')
                        }}>
                            {row.status}
                        </span>
                    </td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"5px"}}>
                            {/* APPROVE (Admin/Account Only) */}
                            {row.status === 'Pending' && canApprove && (
                                <>
                                    <button onClick={() => updateStatus(row.id, 'Approved')} style={btnIconCheck} title="Approve">✔</button>
                                    <button onClick={() => updateStatus(row.id, 'Rejected')} style={btnIconCross} title="Reject">✖</button>
                                </>
                            )}
                            <button onClick={() => setViewData(row)} style={btnIconEye} title="View Details">👁️</button>
                            
                            {(isAdmin || isAccount) && (
                                <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                            )}
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL (Same as before) */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#e74c3c"}}>🧾 Expense Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", fontSize:"13px"}}>
                <div><strong>📅 Date:</strong> <br/>{formatDate(viewData.createdAt)}</div>
                <div><strong>👤 Employee:</strong> <br/>{viewData.userName}</div>
                <div><strong>🏷️ Type:</strong> <br/>{viewData.type}</div>
                <div><strong>💰 Amount:</strong> <br/>₹{viewData.amount}</div>
            </div>

            <div style={{marginTop:"15px", padding:"10px", backgroundColor:"#f9f9f9", borderRadius:"5px", fontSize:"13px"}}>
                <strong>📝 Remarks / Details:</strong>
                <p style={{margin:"5px 0", color:"#555"}}>{viewData.remark || "No remarks"}</p>
                {viewData.type === 'Travel' && (
                    <div style={{marginTop:"5px", color:"#333"}}>
                        🚗 <strong>Route:</strong> {viewData.from} ➝ {viewData.to}<br/>
                        📏 <strong>Distance:</strong> {viewData.distance} km
                    </div>
                )}
            </div>

            {viewData.imageUrl && (
                <div style={{marginTop:"15px", textAlign:"center"}}>
                    <a href={viewData.imageUrl} target="_blank" rel="noreferrer" style={btnLink}>📄 View Attached Receipt</a>
                </div>
            )}

            {viewData.status === 'Pending' && canApprove && (
                <div style={{marginTop:"20px", display:"flex", gap:"10px"}}>
                    <button onClick={()=>{updateStatus(viewData.id, 'Approved'); setViewData(null);}} style={btnBigApprove}>Approve Request</button>
                    <button onClick={()=>{updateStatus(viewData.id, 'Rejected'); setViewData(null);}} style={btnBigReject}>Reject Request</button>
                </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};
const btnIconCheck={backgroundColor:"#e8f5e9", color:"green", border:"1px solid #c8e6c9", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconCross={backgroundColor:"#ffebee", color:"red", border:"1px solid #ffcdd2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEye={backgroundColor:"#e3f2fd", color:"#1976d2", border:"1px solid #bbdefb", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};
const btnLink={display:"block", padding:"10px", backgroundColor:"#34495e", color:"white", textDecoration:"none", borderRadius:"5px", fontWeight:"bold"};
const btnBigApprove={flex:1, padding:"12px", backgroundColor:"#27ae60", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};
const btnBigReject={flex:1, padding:"12px", backgroundColor:"#c0392b", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};
const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};

export default Expenses;